import { useCallback, useEffect, useRef, useState } from 'react';
import { getGraphQLErrorCode } from '@/lib/graphql-error';
import { useRecaptcha } from './useRecaptcha';

export interface EmailCaptcha {
  captcha: string;
  captchaVersion: 'v3' | 'v2';
  captchaAction: string;
}

/** Retry only the mutation after a challenge, never preceding Stripe operations. */
export function useRecaptchaMutation(active = true) {
  const { execute, renderV2Widget, onV2Solved, resetV2: reset } = useRecaptcha();
  const [challengeRequired, setChallengeRequired] = useState(false);
  const activeRef = useRef(active);
  const pending = useRef<((error: Error) => void) | null>(null);

  useEffect(() => {
    activeRef.current = active;
    if (!active) {
      pending.current?.(new Error('Verification cancelled'));
      pending.current = null;
      setChallengeRequired(false);
      reset();
    }
    return () => {
      activeRef.current = false;
      pending.current?.(new Error('Verification cancelled'));
      pending.current = null;
    };
  }, [active, reset]);

  const run = useCallback(
    async <T>(action: string, mutation: (captcha: EmailCaptcha) => Promise<T>): Promise<T> => {
      const token = await execute(action);
      if (!activeRef.current) throw new Error('Verification cancelled');
      if (!token && import.meta.env.VITE_RECAPTCHA_DISABLE !== 'true') {
        throw new Error('reCAPTCHA verification failed. Please try again.');
      }
      try {
        return await mutation({
          captcha: token ?? '',
          captchaVersion: 'v3',
          captchaAction: action,
        });
      } catch (error) {
        if (!activeRef.current) throw new Error('Verification cancelled');
        if (getGraphQLErrorCode(error) !== 'RECAPTCHA_CHALLENGE_REQUIRED') throw error;
        if (!import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
          throw new Error('reCAPTCHA verification is unavailable. Please try again later.');
        }
        try {
          const v2Token = await new Promise<string>((resolve, reject) => {
            pending.current = reject;
            onV2Solved((solvedToken) => {
              if (!pending.current) return;
              pending.current = null;
              resolve(solvedToken);
            });
            setChallengeRequired(true);
          });
          return await mutation({ captcha: v2Token, captchaVersion: 'v2', captchaAction: action });
        } finally {
          setChallengeRequired(false);
          reset();
        }
      }
    },
    [execute, onV2Solved, reset],
  );

  const renderChallenge = useCallback(
    async (container: HTMLElement) => {
      await renderV2Widget(container);
      if (!container.childElementCount) {
        pending.current?.(
          new Error('reCAPTCHA verification is unavailable. Please try again later.'),
        );
        pending.current = null;
      }
    },
    [renderV2Widget],
  );

  return { run, challengeRequired, renderV2Widget: renderChallenge };
}
